import { css } from "@emotion/react";
import type { DoctorsListOrder } from "@hermes/api";
import { useMessage } from "@hermes/intl";
import {
  ButtonV2,
  cssFns,
  fieldState,
  FilterOutlinedButton,
  Select,
  type SelectImperativeRef,
  type SelectOption,
  useFormField,
  useScreenDetector,
  useSelectState,
} from "@hermes/ui";
import { useRef } from "react";

import type { DoctorsListParams } from "./types";

export const SortDesktop = ({
  params,
  onParamsChange,
}: {
  params: DoctorsListParams;
  onParamsChange: (params: DoctorsListParams) => void;
}) => {
  const message = useMessage();
  const { isMobile, isDesktop } = useScreenDetector();

  const options = [
    {
      value: "default",
      label: message({
        id: "726e1cde-dd98-4290-b4b6-91d57e3d03e2",
        default: "По умолчанию",
      }),
    },
    {
      value: "rating",
      label: message({
        id: "b76fa912-3ad1-4457-9029-b77db3eb7f85",
        default: "Рейтингу",
      }),
    },
    {
      value: "price",
      label: message({
        id: "doctors-list.filters.by-price",
        default: "Цене",
      }),
    },
    {
      value: "experience",
      label: message({
        id: "31554e02-7099-4fae-947c-46cca5595ea4",
        default: "Стажу",
      }),
    },
    isMobile && {
      value: "distance",
      label: message({
        id: "doctors-list.filters.near",
      }),
    },
  ].truthy() as SelectOption<DoctorsListOrder>[];

  const selectRef = useRef<SelectImperativeRef>(null);
  const field = useFormField(
    useSelectState<DoctorsListOrder>({
      value: params.sort || "default",
      parent: params.sort,
    }),
  );
  const value = (isDesktop ? params.sort : field.value) || "default";
  const selected =
    options.find((option) => option.value === value) || options[0];

  const handleChange = (value?: DoctorsListOrder) => {
    selectRef.current?.close();
    onParamsChange({
      sort: value,
      nearme: value === "distance",
    });
  };

  return (
    <Select
      imperativeRef={selectRef}
      closeOnSelect={isDesktop}
      field={{
        value: field.value,
        onChange(state) {
          if (isDesktop) {
            handleChange(fieldState(field, state).value ?? undefined);
          }
          field.onChange(state);
        },
      }}
      options={options}
      mobile={{
        title: message({
          id: "18e53d11-6a9f-4677-8aaf-87e8a63afb1a",
          default: "Сортировать по",
        }),
        height: "auto",
        footer: (
          <div
            css={[
              cssFns.padding(8),
              {
                display: "grid",
                width: "100%",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              },
            ]}
          >
            <ButtonV2
              text={message({
                id: "common.cancel",
                default: "Отменить",
              })}
              variant="outlined"
              onPress={() => {
                selectRef.current?.close();
              }}
            />
            <ButtonV2
              text={message({
                id: "9db59481-22a6-4d7c-806f-ae8f4ff52a34",
                default: "Применить",
              })}
              variant="solid"
              onPress={() => {
                handleChange(field.value ?? undefined);
              }}
            />
          </div>
        ),
      }}
    >
      {({ triggerRef, triggerProps }) => (
        <FilterOutlinedButton.Dropdown
          triggerRef={triggerRef}
          triggerProps={triggerProps}
          text={selected?.label || ""}
          active={!!value}
          styles={css({ minWidth: 165 })}
        />
      )}
    </Select>
  );
};
